import { Head, useForm } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { serviceEditorPath, serviceEditorSectionPath, servicePath } from "@travily/routes";
import type ListingsEditorTagline from "@travily/types/pages/ListingsEditorTagline";
import { ButtonDock } from "@ui/components/ButtonDock";
import { StandardButton } from "@ui/components/StandardButton";
import type { ReactNode } from "react";
import { EditFrame } from "@/layouts/edit-frame";
import PersistentLayout from "@/layouts/persistent-layout";
import { buildEditorSections } from "@/lib/editor-sections";
import { firstError } from "@/lib/forms/first-error";
import { ServiceTaglineEditor } from "./show.view";

type PageProps = ListingsEditorTagline;

interface TaglineFormData {
  tagline: string;
}

const ServiceEditTaglinePage = (props: PageProps) => {
  const { listing_id, tagline } = props;
  const sections = buildEditorSections(props);
  const form = useForm<TaglineFormData>({ tagline: tagline ?? "" });

  const handleSave = () => {
    form.patch(serviceEditorSectionPath(listing_id, "tagline"), {
      preserveScroll: "errors",
    });
  };

  return (
    <EditFrame
      activeSection="tagline"
      actions={
        <ButtonDock
          primaryAction={
            <>
              <StandardButton
                className="sm:hidden"
                color="green"
                isDisabled={!form.isDirty || form.processing}
                onPress={handleSave}
                size="56"
              >
                {t("common.actions.save")}
              </StandardButton>
              <StandardButton
                className="hidden sm:flex"
                color="green"
                isDisabled={!form.isDirty || form.processing}
                onPress={handleSave}
                size="64"
              >
                {t("common.actions.save")}
              </StandardButton>
            </>
          }
        />
      }
      backHref={servicePath(listing_id)}
      href={serviceEditorPath(listing_id)}
      sections={sections}
      title={t("web.services.editors.show.title")}
    >
      <Head title={t("web.services.editor.sections.show.meta_title", { title: props.listing_title ?? "" })} />
      <ServiceTaglineEditor
        error={firstError(form.errors.tagline as string | string[] | undefined)}
        onChange={(value) => form.setData("tagline", value)}
        value={form.data.tagline}
      />
    </EditFrame>
  );
};

ServiceEditTaglinePage.layout = (page: ReactNode) => <PersistentLayout>{page}</PersistentLayout>;

export default ServiceEditTaglinePage;
